import { IMarkerPopup } from "@components/blocks/map/markerPopup/MarkerPopup";
import { OnSelect } from "@lib/utils/types/filters/OnSelect";
import { getNavigation } from "@repo/map-helpers";
import { Position } from "geojson";
import { useSearchParams } from "next/navigation";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export interface IMapContext {
  markerPopup: Omit<IMarkerPopup, "onClose" | "navigate"> | null;
  recentlyChosenFilter: OnSelect | null;
  coordinates: Position[] | null;
  navigationType: "biking" | "hiking" | undefined;
  distance: number | null;
  setMarkerPopup: (
    marker: Omit<IMarkerPopup, "onClose" | "navigate"> | null
  ) => void;
  setFilter: (filter: OnSelect | null) => void;
  clearMarkerPopup: () => void;
  navigate(
    longitude: number,
    latitude: number,
    type: "biking" | "hiking"
  ): Promise<void>;
  clearCoordinates: () => void;
}

export interface IMapProvider {
  children: ReactNode;
}

export const mapContext = createContext<IMapContext | undefined>(undefined);

const MapContextProvider = mapContext.Provider;

export const MapProvider = ({ children }: IMapProvider) => {
  const [coordinates, setCoordinates] = useState<Position[] | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const searchParams = useSearchParams();

  const [navigationType, setNavigationType] = useState<
    "biking" | "hiking" | undefined
  >();
  const [markerPopup, setMarker] = useState<Omit<
    IMarkerPopup,
    "onClose" | "navigate"
  > | null>(null);
  const [recentlyChosenFilter, setRecentlyChosenFilter] =
    useState<OnSelect | null>(null);

  useEffect(() => {
    const pathType = searchParams.get("pathType");

    if (pathType) {
      setRecentlyChosenFilter({
        name: "pathType",
        value: pathType,
        type: "single",
      });
    } else {
      setRecentlyChosenFilter(null);
    }
  }, [searchParams]);

  const setMarkerPopup = (
    marker: Omit<IMarkerPopup, "onClose" | "navigate"> | null
  ) => {
    setNavigationType(undefined);
    setMarker(marker);
  };

  const clearCoordinates = () => {
    setCoordinates(null);
    setDistance(null);
  };

  const clearMarkerPopup = () => {
    setNavigationType(undefined);
    setMarker(null);
    setCoordinates(null);
    setDistance(null);
  };

  const setFilter = (filter: OnSelect | null) => {
    setRecentlyChosenFilter(filter);
  };

  async function navigate(
    longitude: number,
    latitude: number,
    type: "biking" | "hiking"
  ) {
    async function calculateDistance(
      userLatitude: number,
      userLongitude: number
    ) {
      const navigation = await getNavigation({
        coordinates: {
          startPosition: [userLongitude, userLatitude] as Position,
          endPosition: [longitude, latitude] as Position,
        },
        type,
      });
      setNavigationType(type);
      setCoordinates(navigation?.coordinates || null);
      setDistance(navigation?.distance || null);
    }
    function handleUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (p: GeolocationPosition) => {
            calculateDistance(p.coords.latitude, p.coords.longitude);
          },
          () => console.error("Unable to retrieve your location")
        );
      } else {
        console.error("Geolocation not supported");
      }
    }
    handleUserLocation();
  }

  return (
    <MapContextProvider
      value={{
        markerPopup,
        recentlyChosenFilter,
        coordinates,
        setMarkerPopup,
        clearMarkerPopup,
        setFilter,
        navigate,
        navigationType,
        clearCoordinates,
        distance,
      }}
    >
      {children}
    </MapContextProvider>
  );
};

export const useMapContext = () => {
  const context = useContext(mapContext);

  if (!context) {
    throw new Error("useMapContext cannot be used outside of MapProvider");
  }

  return context;
};
